import {
  Category,
  type CategoryTreeNode,
  buildCategoryForest,
} from "@/types/category";

export interface FlatCategoryOption {
  name: string;
  depth: number;
  ancestors: string[];
  hasChildren: boolean;
}

/** DFS the category forest into a flat array preserving parent→child order. */
export function flattenCategoryForest<C extends Category>(
  categories: C[],
): FlatCategoryOption[] {
  const forest = buildCategoryForest(categories);
  const flat: FlatCategoryOption[] = [];
  // Depth comes from traversal (ancestors.length) so it's correct regardless of
  // what node.depth says — the forest builder is supposed to set node.depth right,
  // but trusting the walk lets us drop that coupling.
  const walk = (nodes: CategoryTreeNode[], ancestors: string[]) => {
    const sorted = [...nodes].sort((a, b) => a.name.localeCompare(b.name));
    for (const node of sorted) {
      flat.push({
        name: node.name,
        depth: ancestors.length,
        ancestors,
        hasChildren: node.children.length > 0,
      });
      walk(node.children, [...ancestors, node.name]);
    }
  };
  walk(forest, []);
  return flat;
}

/** Map of category name → ancestors-not-including-self, in root-first order. */
export function categoryAncestorsByName(
  categories: Category[],
): Map<string, string[]> {
  const byName = new Map<string, Category>();
  for (const c of categories) byName.set(c.name, c);
  const result = new Map<string, string[]>();
  // Cycle guard handles any length of loop — closure-table writes reject cycles,
  // but legacy data could still have A→B→A or longer rings. Treat anything in the
  // current visiting set as a root to break the chain rather than stack-overflowing.
  const ancestorsOf = (name: string, visiting: Set<string>): string[] => {
    const cached = result.get(name);
    if (cached) return cached;
    if (visiting.has(name)) {
      result.set(name, []);
      return [];
    }
    const node = byName.get(name);
    if (!node || !node.parentCategoryName) {
      result.set(name, []);
      return [];
    }
    visiting.add(name);
    const chain = [
      ...ancestorsOf(node.parentCategoryName, visiting),
      node.parentCategoryName,
    ];
    visiting.delete(name);
    result.set(name, chain);
    return chain;
  };
  for (const c of categories) ancestorsOf(c.name, new Set());
  return result;
}

/** "Injectables › Botox" string, or just the name if no ancestors. */
export function categoryBreadcrumb(
  categories: Category[],
  name: string,
): string {
  const ancestors = categoryAncestorsByName(categories).get(name);
  if (!ancestors?.length) return name;
  return [...ancestors, name].join(" › ");
}

/** Two-space indent per depth level — works in plain-text option labels. */
export function categoryIndentPrefix(depth: number): string {
  return " ".repeat(depth);
}

/** A label for use inside <SelectItem>/<SearchSelectItem> children — non-zero depth
 * gets a tree-line glyph + indentation so the hierarchy is visible at a glance. */
export function categoryIndentedLabel(name: string, depth: number): string {
  if (depth === 0) return name;
  return `${categoryIndentPrefix(depth)}└ ${name}`;
}
